import React from 'react';
import { Tooltip, Box } from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ContactPage as IdCardIcon,
  LocationOn as LocationIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';

interface VerificationBadgeProps {
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  isIdVerified?: boolean;
  isAddressVerified?: boolean;
  verificationLevel?: number;
  size?: 'small' | 'medium';
  showAll?: boolean;
  compact?: boolean;
  firstIdVerifiedDate?: string;
  latestIdVerifiedDate?: string;
  joinedQixitDate?: string;
}

const VerificationBadge: React.FC<VerificationBadgeProps> = ({
  isEmailVerified = false,
  isPhoneVerified = false,
  isIdVerified = false,
  isAddressVerified = false,
  size = 'small',
  showAll = false,
  compact = false,
  firstIdVerifiedDate,
  latestIdVerifiedDate,
  joinedQixitDate,
}) => {
  const formatDate = (value?: string): string | null => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString();
  };

  const iconSize = size === 'small' ? 16 : 20;
  const iconStyle = { width: iconSize, height: iconSize };

  const isFullyVerifiedPersonal =
    isEmailVerified && isPhoneVerified && isIdVerified && isAddressVerified;

  const badges: React.ReactNode[] = [];

  if (isFullyVerifiedPersonal) {
    badges.push(
      <Tooltip
        key="shield"
        title="Email, phone, ID, and address verified"
        arrow
      >
        <ShieldIcon sx={{ ...iconStyle, color: '#FFD700' }} />
      </Tooltip>
    );
  }

  if (isEmailVerified || showAll) {
    badges.push(
      <Tooltip key="email" title={isEmailVerified ? "Email Verified" : "Email Not Verified"} arrow>
        <EmailIcon 
          sx={{ 
            ...iconStyle, 
            color: isEmailVerified ? '#2196F3' : '#9e9e9e',
            opacity: isEmailVerified ? 1 : 0.4
          }} 
        />
      </Tooltip>
    );
  }

  if (isPhoneVerified || showAll) {
    badges.push(
      <Tooltip key="phone" title={isPhoneVerified ? "Phone Verified" : "Phone Not Verified"} arrow>
        <PhoneIcon 
          sx={{ 
            ...iconStyle, 
            color: isPhoneVerified ? '#4CAF50' : '#9e9e9e',
            opacity: isPhoneVerified ? 1 : 0.4
          }} 
        />
      </Tooltip>
    );
  }

  if (isIdVerified || showAll) {
    const firstVerified = formatDate(firstIdVerifiedDate);
    const latestVerified = formatDate(latestIdVerifiedDate);
    const joinedDate = formatDate(joinedQixitDate);
    const idTooltip = isIdVerified
      ? [
          `ID Verified`,
          latestVerified ? `Most recent verification: ${latestVerified}` : null,
          firstVerified && firstVerified !== latestVerified ? `First verification: ${firstVerified}` : null,
          joinedDate ? `Joined Qixit: ${joinedDate}` : null
        ].filter(Boolean).join('\n')
      : "ID Not Verified";
    badges.push(
      <Tooltip key="id" title={idTooltip} arrow>
        <IdCardIcon 
          sx={{ 
            ...iconStyle, 
            color: isIdVerified ? '#2E7D32' : '#9e9e9e',
            opacity: isIdVerified ? 1 : 0.4
          }} 
        />
      </Tooltip>
    );
  }

  if (isAddressVerified || showAll) {
    badges.push(
      <Tooltip key="address" title={isAddressVerified ? "Address Verified" : "Address Not Verified"} arrow>
        <LocationIcon 
          sx={{ 
            ...iconStyle, 
            color: isAddressVerified ? '#FF9800' : '#9e9e9e',
            opacity: isAddressVerified ? 1 : 0.4
          }} 
        />
      </Tooltip>
    );
  }

  if (badges.length === 0 && !showAll) {
    return null;
  }

  if (compact && badges.length > 0) {
    const verifiedCount = [isEmailVerified, isPhoneVerified, isIdVerified, isAddressVerified].filter(Boolean).length;
    if (verifiedCount > 0) {
      return (
        <Tooltip title={`${verifiedCount} verification${verifiedCount > 1 ? 's' : ''} completed`} arrow>
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <VerifiedIcon sx={{ ...iconStyle, color: '#4CAF50' }} />
          </Box>
        </Tooltip>
      );
    }
    return null;
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {badges}
    </Box>
  );
};

export const VerificationLevelBadge: React.FC<{ level: number; size?: 'small' | 'medium' }> = ({ 
  level, 
  size = 'small' 
}) => {
  const iconSize = size === 'small' ? 16 : 20;
  
  const getLevelInfo = (level: number) => {
    switch (level) {
      case 5:
        return { label: 'Legacy level 5 (use financial trust tier)', color: '#9e9e9e' };
      case 4:
        return { label: 'Address Verified', color: '#FF9800' };
      case 3:
        return { label: 'ID Verified', color: '#2E7D32' };
      case 2:
        return { label: 'Phone Verified', color: '#4CAF50' };
      case 1:
        return { label: 'Email Verified', color: '#2196F3' };
      default:
        return { label: 'Unverified', color: '#9e9e9e' };
    }
  };

  const { label, color } = getLevelInfo(level);

  if (level === 0) return null;

  return (
    <Tooltip title={label} arrow>
      <VerifiedIcon sx={{ width: iconSize, height: iconSize, color }} />
    </Tooltip>
  );
};

export default VerificationBadge;
